import React from "react";
import PropTypes from "prop-types";
import { navigate } from "gatsby";
import { resolve } from "styled-jsx/css";
import { Tags } from "../Tags";
import { Card } from "../Card";
import { AuthoringDate, AuthoringInfo } from "./AuthoringInfo";
import { ArticleBlurb } from "./ArticleBlurb";
import { Image } from "../Image";
import { SeriesBadge } from "./SeriesBadge";
import { Link } from "../Link";

const badgeOverflowHeight = "2.2em";

// Use resolve so we do not need a wrapper DOM element for scoped styles
const { className: blurbClassName, styles: blurbStyles } = resolve`
    margin: 1.5em 0 1.5em;
    padding: 0 1.5em;
`;
const { className: authorClassName, styles: authorStyles } = resolve`
    padding: 0 1.5em 1.5em;
`;
const { className: tagsClassName, styles: tagsStyles } = resolve`
    margin: 0 0 1.5em;
    padding: 0 1.5em;
`;
const { className: badgedCardClassName, styles: badgeCardStyles } = resolve`
    position: relative;
    margin-top: ${badgeOverflowHeight};
`;

export function ArticleCard({
    className = "",
    link = null,
    title,
    excerpt,
    tags,
    series,
    heroImage = null,
    author: { name: authorName, avatar: authorAvatar, link: authorLink } = {},
    publishedDate,
    onClick = null,
}) {
    const goToArticle = async (e) => {
        e.stopPropagation();
        await navigate(link);
    };
    const isCustomHero = !heroImage?.src;
    return (
        <Card
            className={`${series ? badgedCardClassName : ""} ${className}`}
            onClick={onClick || (link ? goToArticle : undefined)}
        >
            {heroImage && (
                <div className="article-card__hero-image-container">
                    {isCustomHero ? heroImage : <Image {...heroImage} />}
                </div>
            )}
            {series && (
                <div className="article-card__badge">
                    <SeriesBadge series={series} />
                </div>
            )}
            <ArticleBlurb
                className={blurbClassName}
                summary={excerpt}
                title={title}
                link={link}
            />
            {authorName && (
                <AuthoringInfo
                    className={authorClassName}
                    publishedDate={publishedDate}
                    authorName={authorName}
                    authorAvatar={authorAvatar}
                    authorLink={authorLink}
                />
            )}
            {!authorName && (
                <AuthoringDate
                    className={authorClassName}
                    date={publishedDate}
                />
            )}
            {!!tags?.length && <Tags className={tagsClassName} tags={tags} />}
            <style jsx>{`
                .article-card__hero-image-container {
                    display: block;
                    margin-bottom: 1em;
                    max-height: 16em;
                    overflow: hidden;
                    border-radius: 10px 10px 0 0;
                    // Stop background image on a descendant escaping the
                    // border-radius
                    // https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b
                    position: relative;
                }
                .article-card__badge {
                    width: 5em;
                    height: 5em;
                    position: absolute;
                    top: -${badgeOverflowHeight};
                    right: ${badgeOverflowHeight};
                }
            `}</style>
            {blurbStyles}
            {tagsStyles}
            {authorStyles}
            {badgeCardStyles}
        </Card>
    );
}
ArticleCard.propTypes = {
    className: PropTypes.string,
    link: ArticleBlurb.propTypes.link,
    title: ArticleBlurb.propTypes.title,
    excerpt: ArticleBlurb.propTypes.summary,
    publishedDate: AuthoringInfo.propTypes.publishedDate,
    author: PropTypes.shape({
        name: AuthoringInfo.propTypes.authorName,
        avatar: AuthoringInfo.propTypes.authorAvatar,
        link: AuthoringInfo.propTypes.authorLink,
    }),
    tags: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            link: Link.propTypes.to,
        })
    ),
    series: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
    }),
    heroImage: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
        }),
    ]),
    onClick: PropTypes.func,
};
ArticleCard.defaultProps = {
    className: "",
    link: null,
    series: null,
    heroImage: null,
    author: {},
    onClick: null,
};
